import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import ExportButton from '../../../components/common/ExportButton';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import StatusBadge from '../../../components/common/StatusBadge';
import { exportMatchesApi, getMatchesApi, deleteMatchApi, hardDeleteMatchApi } from '../../../api/match.api';
import { getClubsApi } from '../../../api/club.api';
import { getTournamentsApi } from '../../../api/tournament.api';
import { MATCH_STATUS_LABELS, MATCH_TYPE_LABELS, GENDER_LABELS } from '../../../utils/constants';
import { toJalali } from '../../../utils/jalali';
import MatchForm from '../../../components/forms/MatchForm';
import MatchResultForm from '../../../components/forms/MatchResultForm';
import MatchDetailsModal from '../../../components/common/MatchDetailsModal';
import { TeamCell, ScorePill } from '../../../components/tables/MatchesTable';

export default function MatchesManagement() {
  const [matches, setMatches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [filters, setFilters] = useState({ status: '', type: '', gender: '', season: '', tournamentId: '', search: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [resultMatch, setResultMatch] = useState(null);
  const [detailsMatch, setDetailsMatch] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);

  const loadClubs = useCallback(async () => {
    try {
      const { data } = await getClubsApi({ limit: 100 });
      setClubs(data.data);
    } catch {}
  }, []);

  const loadTournaments = useCallback(async () => {
    try {
      const { data } = await getTournamentsApi({ limit: 100 });
      setTournaments(data.data);
    } catch {}
  }, []);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const { data } = await getMatchesApi({ ...clean, page: pagination.page, limit: pagination.limit });
      setMatches(data.data);
      setPagination(prev => ({ ...prev, total: data.meta.total, totalPages: data.meta.totalPages }));
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت مسابقات');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadClubs(); loadTournaments(); }, []);
  useEffect(() => { loadMatches(); }, [loadMatches]);

  const openCreate = () => {
    setEditingMatch(null);
    setFormOpen(true);
  };

  const openEdit = (match) => {
    setEditingMatch(match);
    setFormOpen(true);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await deleteMatchApi(cancelTarget);
      setSnackbar('مسابقه لغو شد');
      setCancelTarget(null);
      loadMatches();
    } catch (err) {
      setSnackbar(err.response?.data?.message);
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    try {
      await hardDeleteMatchApi(hardDeleteTarget);
      setSnackbar('مسابقه به‌طور کامل حذف شد');
      setHardDeleteTarget(null);
      loadMatches();
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'حذف کامل ناموفق بود');
      setHardDeleteTarget(null);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      SCHEDULED: 'pending',
      PLAYED: 'active',
      CANCELED: 'suspended',
      POSTPONED: 'inactive',
    };
    return <StatusBadge status={map[status] || 'inactive'} label={MATCH_STATUS_LABELS[status]} />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-dark">مدیریت مسابقات</h1>
          <p className="text-sm text-slate-400">مدیریت کامل مسابقات، ویرایش، ثبت نتیجه، لغو و حذف</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton
            fileName="matches.xlsx"
            onExport={() =>
              exportMatchesApi(Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '')))
            }
          />
          <GradientButton onClick={openCreate}>
            <Icon name="plus" className="ml-2 h-4 w-4" />
            ثبت مسابقه
          </GradientButton>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(MATCH_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        >
          <option value="">همه انواع</option>
          {Object.entries(MATCH_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.gender}
          onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
        >
          <option value="">همه جنسیت‌ ها</option>
          {Object.entries(GENDER_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
          value={filters.tournamentId}
          onChange={(e) => setFilters({ ...filters, tournamentId: e.target.value })}
        >
          <option value="">همه تورنمنت‌ ها</option>
          {tournaments.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <Input
          placeholder="جستجو..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="h-11 w-64"
        />
      </div>

      {snackbar && (
        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 animate-fade-in">
          {snackbar}
          <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card overflow-x-auto">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          </div>
        ) : (
          <>
            <table className="w-full text-right text-sm">
              <thead className="bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">عنوان</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">میزبان</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">مهمان</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">گروه</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">نتیجه</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">تاریخ</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">محل</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">وضعیت</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {matches.map((m) => (
                  <tr
                    key={m.id}
                    onClick={() => setDetailsMatch(m)}
                    className="cursor-pointer transition-colors duration-200 hover:bg-emerald-50/50"
                    title="برای مشاهده‌ی جزئیات کامل کلیک کنید"
                  >
                    <td className="px-4 py-3 font-medium">{m.title}</td>
                    <td className="px-4 py-3"><TeamCell club={m.homeClub} /></td>
                    <td className="px-4 py-3"><TeamCell club={m.awayClub} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{m.group?.name || '-'}</td>
                    <td className="px-4 py-3">
                      <ScorePill match={m} />
                    </td>
                    <td className="px-4 py-3">{toJalali(m.date)}</td>
                    <td className="px-4 py-3">{m.location}</td>
                    <td className="px-4 py-3">{getStatusBadge(m.status)}</td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setDetailsMatch(m)}
                          className="rounded p-1 text-sky-500 transition-colors hover:bg-sky-50 hover:text-sky-700"
                          title="جزئیات کامل مسابقه"
                        >
                          <Icon name="eye" className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(m)}
                          className="rounded p-1 text-blue-500 transition-colors hover:bg-blue-50 hover:text-blue-700"
                          title="ویرایش"
                        >
                          <Icon name="edit" className="h-4 w-4" />
                        </button>
                        {m.status !== 'PLAYED' && m.status !== 'CANCELED' && (
                          <button
                            onClick={() => setResultMatch(m)}
                            className="rounded p-1 text-emerald-500 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
                            title="ثبت نتیجه"
                          >
                            <Icon name="check" className="h-4 w-4" />
                          </button>
                        )}
                        {m.status !== 'CANCELED' && (
                          <button
                            onClick={() => setCancelTarget(m.id)}
                            className="rounded p-1 text-amber-500 transition-colors hover:bg-amber-50 hover:text-amber-700"
                            title="لغو مسابقه (قابل بازگشت نیست ولی رکورد باقی می‌ماند)"
                          >
                            <Icon name="close" className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setHardDeleteTarget(m.id)}
                          className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                          title="حذف کامل از دیتابیس"
                        >
                          <Icon name="trash" className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {matches.length === 0 && (
                  <tr>
                    <td colSpan="9" className="py-8 text-center text-slate-400">
                      مسابقه‌ای یافت نشد
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <MatchForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingMatch(null); }}
        onCreated={loadMatches}
        clubs={clubs}
        tournaments={tournaments}
        editingMatch={editingMatch}
      />
      <MatchDetailsModal
        open={Boolean(detailsMatch)}
        onClose={() => setDetailsMatch(null)}
        matchId={detailsMatch?.id}
        initialMatch={detailsMatch}
        isAdmin
      />
      <MatchResultForm open={Boolean(resultMatch)} onClose={() => setResultMatch(null)} onSaved={loadMatches} match={resultMatch} />

      <Modal open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)} title="تأیید لغو مسابقه">
        <p className="text-text-dark/70">
          آیا از لغو این مسابقه مطمئن هستید؟ مسابقه با وضعیت «لغوشده» در سیستم باقی می‌ماند و بعداً قابل مشاهده است.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setCancelTarget(null)}>انصراف</Button>
          <GradientButton variant="danger" onClick={handleCancel}>تأیید لغو</GradientButton>
        </div>
      </Modal>

      <Modal open={Boolean(hardDeleteTarget)} onClose={() => setHardDeleteTarget(null)} title="تأیید حذف کامل مسابقه">
        <p className="text-text-dark/70">
          این عملیات مسابقه، نتیجه‌ی ثبت‌شده‌ی آن، و آمار بازیکنانش را <b>به‌طور کامل و بدون بازگشت</b> از دیتابیس پاک می‌کند. اگر فقط می‌خواهید مسابقه دیگر برگزار نشود، بهتر است به‌جای این کار، آن را «لغو» کنید.
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setHardDeleteTarget(null)}>انصراف</Button>
          <GradientButton variant="danger" onClick={handleHardDelete}>حذف کامل</GradientButton>
        </div>
      </Modal>
    </div>
  );
}
